/**
 * m3 echo Frontend - Monthly Quota Panel
 *
 * Two bars, because one configuration produces two different numbers. An
 * autopilot activated mid-month books only the runs actually left, so the
 * opening month is prorated; from the next month on it costs the flat
 * full-month price. Showing either number alone is the single most reliable way
 * to have the model misread, so both are always on screen at once.
 *
 * This is the only place that layout is defined - other views compose this
 * panel instead of building their own bars.
 *
 * One snapshot governs the whole panel. `quota` and `preview` are two
 * responses fetched at different instants, and the bars used to take their
 * geometry from the first while the sentence beneath them quoted the second:
 * quota reports 400 free, somebody else spends 390, the preview correctly
 * refuses a 20-prompt booking, and the bar still drew 380 free right beside
 * the refusal. Whenever a preview is present it is the newer read *and* the
 * one the server's decision was made against, so it sets the limit and the
 * free space, and `quota` contributes only the booked/consumed split inside
 * that total - and only for as long as that split still adds up to it.
 */

import React from 'react';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import {
  AutopilotQuota,
  BookingPreview,
  TenantAutopilotQuota,
} from '../../services/autopilots';
import QuotaBar, { QuotaSegment, safeValue } from './QuotaBar';
import QuotaRefusal, { refusalReasons } from './QuotaRefusal';

/**
 * Both quota shapes are accepted. The admin endpoint omits
 * `my_autopilots_count` and adds `tenant_name`; the panel reads neither, so a
 * union keeps both call sites cast-free.
 */
export type QuotaSource = AutopilotQuota | TenantAutopilotQuota;

interface Props {
  quota: QuotaSource;
  preview?: BookingPreview | null;
}

const MONTH_PATTERN = /^(\d{4})-(\d{2})/;

/**
 * `usage_month` is a plain date and `reset_at` an offset-aware timestamp at
 * midnight in the *tenant's* timezone. Handing that to `Date` and formatting in
 * the browser's zone can roll midnight back into the previous month, naming
 * August where the server meant September, so the calendar month is read
 * straight out of the string and formatted pinned to UTC.
 *
 * Returns `null` on anything it cannot read. Echoing the input back would put
 * `Ab 2026-09-01T00:00:00+02:00 (Dauerzustand)` in a heading; the callers below
 * drop the parenthetical instead, which stays true whatever the server sends.
 */
const monthName = (isoDate: string): string | null => {
  const parts = MONTH_PATTERN.exec(isoDate);
  if (!parts) return null;
  const month = Number(parts[2]);
  if (month < 1 || month > 12) return null;
  const date = new Date(Date.UTC(Number(parts[1]), month - 1, 1, 12));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('de-DE', { month: 'long', timeZone: 'UTC' });
};

export const MonthlyQuotaPanel: React.FC<Props> = ({ quota, preview }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /**
   * The limit both bars are drawn against, taken from the preview when there
   * is one - the same number its refusal quotes.
   */
  const limit = safeValue(preview ? preview.monthly_limit : quota.monthly_limit);

  const previewBooking = preview ? safeValue(preview.initial_booking) : 0;
  /** What the current month still has free, per the snapshot in force. */
  const freeNow = preview ? safeValue(preview.available_now) : safeValue(quota.available);

  /**
   * How much of the month is already spoken for. With a preview on screen this
   * is the preview's own figure, so the bar cannot contradict the sentence
   * under it.
   *
   * `quota` supplies the booked/consumed split, but only while it still adds
   * up to that total. When it does not - somebody else spent in between, or an
   * admin correction released quota - the split is not wrong so much as
   * incomplete, and there is no honest way to attribute the difference: it may
   * have been booked or it may have been run. An earlier attempt gave the
   * difference its own segment, which put a second label on the same dark ink
   * as "gebucht" and made the bar claim a distinction it could not draw. So
   * the two collapse into one committed segment named for what is actually
   * known - "belegt" - and the familiar split returns as soon as the two
   * snapshots agree again, which is every ordinary render.
   */
  const reserved = safeValue(quota.reserved);
  const consumed = safeValue(quota.consumed);
  const occupied = preview ? Math.max(0, limit - freeNow) : reserved + consumed;
  const splitHolds = occupied === reserved + consumed;

  // The preview is taken out of what is still free, so the bar only overflows
  // when the booking genuinely does not fit - not merely because a further
  // segment was added to a bar that was already full.
  const currentSegments: QuotaSegment[] = [
    ...(splitHolds
      ? [
          { key: 'reserved', value: reserved, label: 'gebucht', variant: 'reserved' as const },
          { key: 'consumed', value: consumed, label: 'verbraucht', variant: 'consumed' as const },
        ]
      : [{ key: 'occupied', value: occupied, label: 'belegt', variant: 'reserved' as const }]),
    ...(preview
      ? [
          {
            key: 'preview',
            value: previewBooking,
            label: 'geplant',
            variant: 'preview' as const,
          },
        ]
      : []),
    {
      key: 'free',
      value: Math.max(0, freeNow - previewBooking),
      label: 'frei',
      variant: 'free',
    },
  ];

  /**
   * The second bar splits committed from proposed exactly as the first does.
   * It used to render `preview.steady_state_cost` as one solid "gebucht"
   * block, which draws a cost nobody has agreed to yet as one that is already
   * on the books - and it is the bar this whole two-bar design exists for, the
   * number a customer only otherwise meets in a rejection a month later.
   * `steady_state_cost` is everything else the tenant runs plus this
   * configuration's full month, so subtracting `full_month_cost` leaves
   * precisely the committed part.
   */
  const steadyProposed = preview ? safeValue(preview.full_month_cost) : 0;
  const steadyCommitted = preview
    ? Math.max(0, safeValue(preview.steady_state_cost) - steadyProposed)
    : safeValue(quota.steady_state_cost);
  const steadySegments: QuotaSegment[] = [
    { key: 'steady-booked', value: steadyCommitted, label: 'gebucht', variant: 'reserved' },
    ...(preview
      ? [
          {
            key: 'steady-preview',
            value: steadyProposed,
            label: 'geplant',
            variant: 'preview' as const,
          },
        ]
      : []),
    {
      key: 'steady-free',
      value: Math.max(0, limit - steadyCommitted - steadyProposed),
      label: 'frei',
      variant: 'free',
    },
  ];

  const currentMonth = monthName(quota.usage_month);
  const steadyMonth = monthName(quota.reset_at);
  const currentLabel = currentMonth ? `Dieser Monat (${currentMonth})` : 'Dieser Monat';
  const steadyLabel = steadyMonth
    ? `Ab ${steadyMonth} (Dauerzustand)`
    : 'Ab dem Folgemonat (Dauerzustand)';

  /**
   * One scale for both bars. Each bar would otherwise normalise to its own
   * content, so two meters both labelled 390 would put their limit rules at
   * different x positions the moment they overran by different amounts - and
   * making one limit legible across two months is the entire point of the
   * panel.
   */
  const totalOf = (rows: QuotaSegment[]) =>
    rows.reduce((sum, segment) => sum + safeValue(segment.value), 0);
  const sharedScale = Math.max(limit, totalOf(currentSegments), totalOf(steadySegments));

  return (
    <Stack spacing={isMobile ? 2.5 : 3} sx={{ width: '100%' }}>
      <QuotaBar
        limit={limit}
        segments={currentSegments}
        label={currentLabel}
        scale={sharedScale}
      />
      <QuotaBar
        limit={limit}
        segments={steadySegments}
        label={steadyLabel}
        scale={sharedScale}
      />
      {/*
        The reason is the preview's, not the bars'. It is rendered from the
        shared piece so that a caller which cannot show this panel - a failed
        quota read - still shows the identical refusal rather than none.
      */}
      <QuotaRefusal reasons={refusalReasons(preview)} />
    </Stack>
  );
};

export default MonthlyQuotaPanel;
